// schemas/client.ts
import { z } from 'zod'
import {
  ApiCollectionResponseSchema,
  ApiPaginatedResponseSchema,
  ApiSingleResponseSchema,
} from './api'
import type {
  ApiCollectionResponse,
  ApiPaginatedResponse,
  ApiSingleResponse,
} from './api'
import { GenericCompanySchema } from './generics'
import { UserReferenceSchema } from './user'
import { SubscriberReferenceSchema } from './subscriber'

export const ClientStatisticsSchema = z.object({
  workers: z
    .object({
      total: z.number(),
      active: z.number(),
      by_gender: z
        .object({
          male: z.number(),
          female: z.number(),
        })
        .optional(),
    })
    .optional(),
  users: z
    .object({
      total: z.number(),
      active: z.number(),
      by_role: z.record(z.string(), z.number()).optional(),
    })
    .optional(),
  documents: z
    .object({
      total: z.number(),
      by_type: z.record(z.string(), z.number()).optional(),
      expiring_soon: z.number(),
    })
    .optional(),
  trainings: z
    .object({
      completed: z.number(),
      in_progress: z.number(),
      overdue: z.number(),
    })
    .optional(),
})

// =====================================
// LOGO SCHEMA
// =====================================

const LogoVariantSchema = z.object({
  exists: z.boolean(),
  url: z.string().nullable().optional(),
  thumb_url: z.string().nullable().optional(),
  favicon_url: z.string().nullable().optional(),
})

const ClientLogoSchema = z.object({
  horizontal: LogoVariantSchema.optional(),
  icon: LogoVariantSchema.optional(),
}).nullable().optional()

// =====================================
// MAIN CLIENT SCHEMA
// =====================================

export const ClientSchema = GenericCompanySchema.extend({
  logo: ClientLogoSchema,
  subscriber_id: z.number().int().positive(),
  licence_no: z.string().max(64).nullable().optional(),
  licence_issued_at: z.coerce.date().nullable().optional(),
  is_active: z.boolean().optional(),

  // Relations
  subscriber: SubscriberReferenceSchema.optional(),
  created_by: UserReferenceSchema.optional(),
  updated_by: UserReferenceSchema.optional(),

  // Counts
  workers_count: z.number().nullable().optional(),
  users_count: z.number().nullable().optional(),
  documents_count: z.number().nullable().optional(),

  // Statistics
  statistics: ClientStatisticsSchema.optional(),

  // Timestamps
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
})

export type Client = z.infer<typeof ClientSchema>

// =====================================
// API RESPONSE SCHEMAS
// =====================================

// Single client response: { data: Client }
export const ClientApiResponseSchema = ApiSingleResponseSchema(ClientSchema)
export type ClientApiResponse = ApiSingleResponse<Client>

// Multiple clients response: { data: Client[] }
export const ClientsApiResponseSchema =
  ApiCollectionResponseSchema(ClientSchema)
export type ClientsApiResponse = ApiCollectionResponse<Client>

// Paginated clients response: { data: Client[], meta: {...} }
export const ClientsPaginatedApiResponseSchema =
  ApiPaginatedResponseSchema(ClientSchema)
export type ClientsPaginatedApiResponse = ApiPaginatedResponse<Client>

// =====================================
// CRUD SCHEMAS
// =====================================

export const CreateClientSchema = ClientSchema.omit({
  id: true,
  logo: true,
  subscriber_id: true,
  subscriber: true,
  created_by: true,
  updated_by: true,
  workers_count: true,
  users_count: true,
  documents_count: true,
  statistics: true,
  created_at: true,
  updated_at: true,
})

export type CreateClient = z.infer<typeof CreateClientSchema>

export const UpdateClientSchema = ClientSchema.omit({
  id: true,
  subscriber_id: true,
  created_at: true,
  updated_at: true,
})

export type UpdateClient = z.infer<typeof UpdateClientSchema>

// =====================================
// VALIDATION UTILITIES
// =====================================

// Direct validation functions
export const validateClient = (data: unknown): Client => {
  return ClientSchema.parse(data)
}

export const validateCreateClient = (data: unknown): CreateClient => {
  return CreateClientSchema.parse(data)
}

export const validateUpdateClient = (data: unknown): UpdateClient => {
  return UpdateClientSchema.parse(data)
}

// API response validation functions
export const validateClientApiResponse = (data: unknown): Client => {
  const response = ClientApiResponseSchema.parse(data)
  return response.data
}

export const validateClientsPaginatedApiResponse = (
  data: unknown
): ClientsPaginatedApiResponse => {
  return ClientsPaginatedApiResponseSchema.parse(data)
}

// Safe validation functions
export const safeValidateClient = (data: unknown) => {
  return ClientSchema.safeParse(data)
}

export const safeValidateClientApiResponse = (data: unknown) => {
  const result = ClientApiResponseSchema.safeParse(data)
  if (result.success) {
    return { success: true, data: result.data.data }
  }
  return { success: false, error: result.error }
}

// =====================================
// CLIENT INFO STATS SCHEMA
// =====================================

export const ClientInfoStatsSchema = z.object({
  safety_acts_count: z.number().int().nonnegative(),
  departments_count: z.number().int().nonnegative(),
  job_positions_count: z.number().int().nonnegative(),
  job_positions_increased_risk_count: z.number().int().nonnegative(),
  workers_count: z.number().int().nonnegative(),
  job_contracts_count: z.number().int().nonnegative(),
  health_institutions_count: z.number().int().nonnegative(),
})

export const ClientInfoStatsResponseSchema = ApiSingleResponseSchema(
  ClientInfoStatsSchema
)

export type ClientInfoStats = z.infer<typeof ClientInfoStatsSchema>
export type ClientInfoStatsResponse = ApiSingleResponse<ClientInfoStats>

// =====================================
// REFERENCE SCHEMA
// =====================================

export const ClientReferenceSchema = z.object({
  id: z.number(),
  name: z.string(),
  short_name: z.string().optional(),
  is_active: z.boolean(),
})

export type ClientReference = z.infer<typeof ClientReferenceSchema>
